/**
 * useSelectedDevice Hook
 * 
 * Purpose:
 * Manages the currently selected device for the devices view.
 * This hook provides a centralized way to track which device is currently
 * selected and persists the selection across browser sessions.
 * 
 * Usage Example:
 * ```tsx
 * import { useSelectedDevice } from '../features/devices/hooks/useSelectedDevice';
 * 
 * function MyComponent() {
 *   const { selectedDeviceId, setSelectedDeviceId } = useSelectedDevice();
 * 
 *   return <div>Content for device {selectedDeviceId}</div>;
 * }
 * ```
 * 
 * Key Features:
 * - Persists selection to localStorage for session continuity
 * - Provides context to all components via DeviceProvider
 * - Type-safe with TypeScript
 * - Throws error if used outside DeviceProvider
 */

import { createContext, useState, useEffect, useCallback, useContext, ReactNode } from 'react';

const STORAGE_KEY = 'zentrian_selected_device';

const getInitialSelectedDevice = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const storedDeviceId = window.localStorage.getItem(STORAGE_KEY);
    return storedDeviceId || null;
  } catch (error) {
    console.error('Failed to read selected device ID from localStorage', error);
  }
  return null;
};

interface DeviceContextValue {
  selectedDeviceId: string | null;
  setSelectedDeviceId: (deviceId: string | null) => void;
}

const DeviceContext = createContext<DeviceContextValue | undefined>(undefined);

export const DeviceProvider = ({ children }: { children: ReactNode }) => {
  const [selectedDeviceId, setSelectedDeviceIdState] = useState<string | null>(getInitialSelectedDevice);

  useEffect(() => {
    try {
      if (selectedDeviceId === null) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, selectedDeviceId);
      }
    } catch (error) {
      console.error('Failed to save selected device ID to localStorage', error);
    }
  }, [selectedDeviceId]);

  const setSelectedDeviceId = useCallback((deviceId: string | null) => {
    setSelectedDeviceIdState(deviceId);
  }, []);

  const value: DeviceContextValue = {
    selectedDeviceId,
    setSelectedDeviceId,
  };

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
};

export const useSelectedDevice = () => {
  const context = useContext(DeviceContext);
  if (context === undefined) {
    throw new Error('useSelectedDevice must be used within a DeviceProvider');
  }
  return context;
};
